import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { discordLoginUrl, meKey, microsoftLoginUrl, useAuthOptions } from '../lib/session';
import type { Device, WebMe } from '../lib/types';
import { Button } from '../ui/controls';
import { ConfirmDialog, RenameDialog } from '../ui/dialog';
import { ErrorNotice, Notice, useToast } from '../ui/feedback';
import { DefList, Tag, Time, Uuid, VerifiedTag } from '../ui/values';

/**
 * Bausteine des Spieler-Portals (Revision R1/R2): Konten entstehen im Spiel (Mod + Beta-Code).
 * Browser-Login per Einmal-Link aus der Mod oder „Mit Microsoft anmelden“; Admins zusätzlich per Discord.
 * Im Browser: Konto ansehen, Geräte verwalten, Konto löschen; Admins können Discord verknüpfen.
 */

/** Befehl in der Mod, der den Einmal-Link für den Browser holt (architecture.md 4.2). */
export const WEB_COMMAND = '/chestcatalog web';

export const LOGIN_ERRORS: Record<string, string> = {
  link: 'Der Link ist abgelaufen oder wurde schon benutzt. Hol dir im Spiel einen neuen.',
  suspended: 'Dieses Konto ist gesperrt.',
  not_linked: 'Dieses Discord-Konto ist mit keinem Admin verknüpft. Melde dich per Link aus dem Spiel an.',
  not_admin: 'Nur Admins können Discord verknüpfen. Melde dich zuerst per Link aus dem Spiel an.',
  discord_taken: 'Dieses Discord-Konto ist schon mit einem anderen Konto verknüpft.',
  discord_disabled: 'Der Discord-Login ist auf diesem Server nicht eingerichtet.',
  denied: 'Die Anmeldung bei Discord wurde abgebrochen.',
  state: 'Die Anmeldung ist abgelaufen. Bitte noch einmal versuchen.',
  code: 'Discord hat die Anmeldung nicht bestätigt. Bitte noch einmal versuchen.',
  discord: 'Discord ist gerade nicht erreichbar. Bitte später noch einmal versuchen.',
  no_account: 'Für diesen Minecraft-Account gibt es noch kein Konto. Installiere die Mod und melde dich dort mit deinem Beta-Code an.',
  microsoft_disabled: 'Der Microsoft-Login ist auf diesem Server nicht eingerichtet.',
  ms_denied: 'Die Anmeldung bei Microsoft wurde abgebrochen.',
  ms_code: 'Microsoft hat die Anmeldung nicht bestätigt. Bitte noch einmal versuchen.',
  ms_no_xbox: 'Zu diesem Microsoft-Konto gibt es kein Xbox-Profil. Melde dich einmal auf xbox.com an und versuche es dann erneut.',
  ms_no_minecraft: 'Zu diesem Microsoft-Konto gehört kein Minecraft: Java Edition.',
  ms_not_approved: 'Der Microsoft-Login ist noch nicht freigeschaltet. Nimm solange den Login über Minecraft.',
  ms_unavailable: 'Microsoft oder Minecraft ist gerade nicht erreichbar. Bitte später noch einmal versuchen.',
};

export function LoginSection({ error, returnTo }: { error?: string | null; returnTo?: string }) {
  const options = useAuthOptions();
  return (
    <section>
      <h1>Anmelden</h1>
      {error && <Notice>{LOGIN_ERRORS[error] ?? 'Die Anmeldung hat nicht geklappt. Bitte noch einmal versuchen.'}</Notice>}
      <p className="lead">Chest Catalog läuft über deinen Minecraft-Account.</p>
      {options.data?.microsoft && (
        <>
          <h2>Mit Microsoft</h2>
          <p>
            <a className="btn" href={microsoftLoginUrl(returnTo)}>
              Mit Microsoft anmelden
            </a>
          </p>
        </>
      )}
      {options.data?.microsoft ? <h2>Mit der Mod</h2> : <p>Die Anmeldung im Browser holst du dir im Spiel:</p>}
      <ol className="steps">
        <li>
          Im Spiel <span className="mono">{WEB_COMMAND}</span> eingeben.
        </li>
        <li>Den Link im Chat anklicken. Er gilt 5 Minuten und nur einmal.</li>
      </ol>
      <p className="muted">Neu dabei? Installiere die Mod. Beim ersten Start fragt sie nach deinem Beta-Code.</p>
      {options.data?.discord && (
        <p>
          <a className="btn" href={discordLoginUrl(returnTo)}>
            Admins: mit Discord anmelden
          </a>
        </p>
      )}
      {import.meta.env.DEV && (
        <Notice tone="warn">
          <span>
            Nur lokal (Dev-Server, Daten aus <span className="mono">npm run db:seed</span>): anmelden als{' '}
            <a href="/__dev/login?as=admin">Admin</a> · <a href="/__dev/login?as=player">Spieler</a>
          </span>
        </Notice>
      )}
    </section>
  );
}

export function AccountSection({ me }: { me: WebMe }) {
  const mc = me.mcAccount;
  const qc = useQueryClient();
  const toast = useToast();
  const [renaming, setRenaming] = useState(false);
  return (
    <section>
      <h2>Konto</h2>
      <DefList
        items={[
          ['Minecraft', mc ? <><b>{mc.name}</b> <VerifiedTag verified={mc.verified} /></> : <span className="muted">–</span>],
          ['UUID', mc ? <Uuid value={mc.uuid} /> : <span className="muted">–</span>],
          [
            'Team',
            me.team ? (
              <>
                {me.team.name} {me.team.isOwner && <Tag tone="neutral">Besitzer</Tag>}{' '}
                <span className="muted">· {me.team.memberCount} Mitglieder</span>
                {me.team.isOwner && me.status === 'active' && (
                  <>
                    {' '}
                    <Button variant="quiet" className="compact" onClick={() => setRenaming(true)}>
                      Umbenennen…
                    </Button>
                  </>
                )}
              </>
            ) : (
              <span className="muted">kein Team (in der Mod beitreten oder erstellen)</span>
            ),
          ],
          ['Dabei seit', mc ? <Time iso={mc.linkedAt} absolute /> : <span className="muted">–</span>],
        ]}
      />
      {renaming && me.team && (
        <RenameDialog
          title="Team umbenennen"
          label="Teamname"
          current={me.team.name}
          maxLength={32}
          needReason={false}
          help="1–32 Zeichen. Alle Mitglieder sehen den neuen Namen."
          onClose={() => setRenaming(false)}
          onSave={async (name) => {
            qc.setQueryData(meKey, await api.patch<WebMe>('/web/me/team', { name }));
            toast('Team umbenannt.');
          }}
        />
      )}
    </section>
  );
}

export function DevicesSection() {
  const qc = useQueryClient();
  const toast = useToast();
  const devices = useQuery({ queryKey: ['devices'], queryFn: () => api.get<{ items: Device[] }>('/web/me/devices') });
  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/web/me/devices/${id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['devices'] });
      toast('Gerät abgemeldet.');
    },
  });

  return (
    <section>
      <h2>Geräte</h2>
      <p className="lead">Hier ist die Mod angemeldet. Abmelden beendet die Anmeldung auf diesem Gerät sofort.</p>
      {devices.isError && <ErrorNotice error={devices.error} onRetry={() => devices.refetch()} />}
      {devices.data?.items.length === 0 && <p className="muted">Die Mod ist auf keinem Gerät angemeldet.</p>}
      {devices.data && devices.data.items.length > 0 && (
        <ul className="list-plain">
          {devices.data.items.map((d) => (
            <li key={d.id}>
              <div className="grow">
                <span>{d.clientVersion ? <span className="mono">{d.clientVersion}</span> : <b>Mod</b>}</span>
                <span className="muted">
                  angemeldet <Time iso={d.linkedAt} /> · zuletzt aktiv <Time iso={d.lastUsedAt} />
                </span>
              </div>
              <Button onClick={() => revoke.mutate(d.id)} busy={revoke.isPending && revoke.variables === d.id} busyText="Wird abgemeldet…">
                Abmelden
              </Button>
            </li>
          ))}
        </ul>
      )}
      {revoke.isError && <ErrorNotice error={revoke.error} />}
    </section>
  );
}

/** Nur für Admins: Discord-Login verknüpfen bzw. lösen (architecture.md 4.3). */
export function DiscordSection({ me }: { me: WebMe }) {
  const qc = useQueryClient();
  const toast = useToast();
  const options = useAuthOptions();
  const unlink = useMutation({
    mutationFn: () => api.delete('/web/me/discord'),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: meKey });
      toast('Discord-Verknüpfung gelöst.');
    },
  });
  if (!me.isAdmin || !options.data?.discord) return null;

  return (
    <section>
      <h2>Discord-Login</h2>
      {me.discord ? (
        <>
          <p className="lead">
            Verknüpft mit <b>{me.discord.name ?? 'Discord'}</b>. Du kannst dich damit ohne Spiel im Admin-Bereich anmelden.
          </p>
          <div>
            <Button onClick={() => unlink.mutate()} busy={unlink.isPending} busyText="Wird gelöst…">
              Verknüpfung lösen
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className="lead">Verknüpfe Discord, um dich als Admin auch ohne Spiel anzumelden.</p>
          <p>
            <a className="btn" href={discordLoginUrl('/', true)}>
              Discord verknüpfen
            </a>
          </p>
        </>
      )}
      {unlink.isError && <ErrorNotice error={unlink.error} />}
    </section>
  );
}

export function DeleteAccountSection() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  return (
    <section>
      <h2>Konto löschen</h2>
      <p className="lead">Du kannst dein Konto jederzeit löschen. Das ist endgültig.</p>
      <div>
        <Button variant="danger" onClick={() => setOpen(true)}>
          Konto löschen…
        </Button>
      </div>
      <ConfirmDialog
        open={open}
        title="Konto endgültig löschen"
        consequence="Löscht dein Konto mit allen Kisten und Geräten. Besitzt du ein Team, geht es an das Mitglied, das am längsten dabei ist. Für ein neues Konto brauchst du wieder einen Beta-Code. Nicht umkehrbar."
        confirmText="LÖSCHEN"
        needReason={false}
        confirmLabel="Konto löschen"
        onConfirm={async () => {
          await api.delete('/web/me');
          qc.setQueryData(meKey, null);
        }}
        onClose={() => setOpen(false)}
      />
    </section>
  );
}
